import * as React from 'react'
import {
  abortIngestUpload,
  invalidateGraphData,
  isIngestJobFinished,
  useAppDispatch,
  useCancelIngestJobMutation,
  useGetAuthzMeQuery,
  useGetIngestJobsPageQuery,
  useRenameIngestRunMutation
} from '@axilon-apps/domains-ontology-ui-state'
import type { JobListItem } from '@axilon-apps/domains-ontology-ui-state'
import { Card } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger
} from '../../../components/ui/context-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../../../components/ui/alert-dialog'
import { Empty } from '../../../components/primitives/Empty'
import {
  findRunForJob,
  groupIngestRuns,
  runStatusLabel,
  runStatusPill,
  type IngestRunRow
} from '../util/groupIngestRuns'
import { mergeIngestJobItems, type IngestJobRow, type IngestJobKind } from '../util/mergeIngestJobs'
import { nestPlcZipJobs } from '../util/nestPlcZipJobs'
import { stateToProgress, statusToBar, statusToLabel, statusToPill } from '../util/jobStatusMap'
import type { IngestJobState } from '../util/jobStatusMap'
import { Bar } from '../../../components/primitives/Bar'
import { StatusPill } from '../../../components/primitives/StatusPill'
import { useDebugMode } from '../../../lib/debug-mode'
import { formatRelativeTime } from '../../../lib/time'
import { cn } from '../../../lib/utils'
import { ArrowUpRight, PencilLine, Plus, Trash2 } from 'lucide-react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { IngestJobDeleteDialog } from '../../../components/IngestSession/IngestJobDeleteDialog'
import { WriteButton } from '../shared/WriteButton'
import { JobDetailPanel } from './JobDetailPanel'
import { jobBelongsToSiteFilter } from '../wizard/siteFilter'
import { siteOptionLabel } from '../wizard/siteIds'
import {
  INGEST_SESSION_FOCUS_STATE,
  ingestFullSessionPath
} from '../../../components/IngestSession/ingest-session-route'
import { tabularConfigBuildPath, tabularJobPath } from '../../TabularIngestJobPage/route'
import { configBuildPill, tabularJobPill } from '../../TabularIngestJobPage/tabularJobPill'
import { RenameIngestRunDialog } from './RenameIngestRunDialog'

export type JobsScope = 'branch' | 'all'

interface JobsTableProps {
  scope?: JobsScope
  activeBranchId?: string
  /** Optional siteId → display name map for the Site column. */
  siteNames?: Record<string, string>
  /** When set, only show runs that include a job for this site. */
  siteId?: string
  /** Display name for the selected site (URN ↔ slug matching). */
  siteName?: string
}

const COLUMN_COUNT = 6
const EXPAND_MS = 200
const PAGE_SIZE = 30

// Rows in any of these states keep polling at 2 s so progress / log
// updates stream live. `cancelling` is included because it is a
// transitional state — the worker hasn't yet observed the abort signal
// and written terminal `cancelled`. `cancelled` itself is terminal and
// must not poll. `uploaded` is a brief post-unzip handoff before
// `queued` and stays active so the list does not stall in that gap.
const isActiveStatus = (state: IngestJobState) =>
  state === 'uploading' ||
  state === 'queued' ||
  state === 'processing' ||
  state === 'uploaded' ||
  state === 'classifying' ||
  state === 'classified' ||
  state === 'extracting' ||
  state === 'ingested' ||
  state === 'awaiting_user' ||
  state === 'awaiting_rebuild' ||
  state === 'transforming' ||
  state === 'cancelling'

interface ExpansionRowProps {
  run: IngestRunRow
  isOpen: boolean
  expandedJob: { kind: IngestJobKind; id: string } | null
  onJobClick: (row: IngestJobRow) => void
  onCancelClick?: (row: IngestJobRow) => void
  onDeleteClick?: (row: IngestJobRow) => void
  canManage: boolean
}

/**
 * Type cell for rows that open their own page. The column is 130px wide, so
 * the link label is just the kind; the config name goes on a truncated
 * sub-line and the full text lives in the link's `title`.
 */
export function pageTypeCell(row: IngestJobRow): {
  label: string
  subLine?: string
  title: string
} {
  if (row.tabular) {
    const config = `${row.tabular.configName} @v${row.tabular.configVersion}`
    return {
      label: 'Tabular',
      subLine: `${config} · ${row.tabular.sourceLabel}`,
      title: `Tabular ingest · ${config} · ${row.tabular.sourceLabel}`
    }
  }
  if (row.configBuild) {
    const mode = row.configBuild.mode === 'update' ? 'update' : 'new config'
    return {
      label: 'Config build',
      subLine: `${row.configBuild.configName} · ${mode}`,
      title: `Config build · ${row.configBuild.configName} · ${mode}`
    }
  }
  return { label: 'Unstructured', title: 'Unstructured ingest' }
}

function JobTableRow({
  row,
  nested,
  open,
  onJobClick,
  onCancelClick,
  onDeleteClick,
  canManage
}: {
  row: IngestJobRow
  nested?: boolean
  open: boolean
  onJobClick: (row: IngestJobRow) => void
  onCancelClick?: (row: IngestJobRow) => void
  onDeleteClick?: (row: IngestJobRow) => void
  canManage: boolean
}) {
  const navigate = useNavigate()
  const unstructuredHref = row.kind === 'Unstructured' ? ingestFullSessionPath(row.id) : null
  const tabularHref = row.kind === 'MappedTabularIngest' ? tabularJobPath(row.id) : null
  const configBuildHref = row.configBuild
    ? tabularConfigBuildPath(row.configBuild.configName, row.id)
    : null
  const pageHref = unstructuredHref ?? tabularHref ?? configBuildHref
  // The unstructured session page reads its focus marker from route state.
  const pageState = unstructuredHref ? INGEST_SESSION_FOCUS_STATE : undefined
  const tabularPill = row.tabular
    ? tabularJobPill(row.state, row.tabular.acknowledged, row.tabular.parserKind)
    : row.configBuild
      ? configBuildPill(row.state, row.configBuild.phase)
      : undefined
  const tabularRoute = row.kind === 'MappedTabularIngest' || row.kind === 'TabularConfigBuild'
  const typeCell = pageTypeCell(row)
  const showStop = !!onCancelClick && !isIngestJobFinished(row.state)

  // Keep detail mounted through the close animation so collapse is smooth.
  const [detailMounted, setDetailMounted] = React.useState(open)
  React.useEffect(() => {
    if (open) {
      setDetailMounted(true)
      return undefined
    }
    const timer = setTimeout(() => setDetailMounted(false), EXPAND_MS)
    return () => clearTimeout(timer)
  }, [open])

  return (
    <>
      <tr
        role="button"
        tabIndex={0}
        aria-expanded={open}
        data-nested={nested || undefined}
        onClick={() => {
          if (pageHref) {
            navigate(pageHref, { state: pageState })
            return
          }
          if (tabularRoute) return
          onJobClick(row)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            if (pageHref) {
              navigate(pageHref, { state: pageState })
              return
            }
            if (tabularRoute) return
            onJobClick(row)
          }
        }}
        className={cn(
          'cursor-pointer border-b border-line transition-colors hover:bg-bg-sunk/60 focus-visible:bg-bg-sunk/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30',
          nested && 'bg-bg-sunk/30',
          open && 'bg-bg-sunk/60'
        )}
      >
        <td className={cn('px-3 py-2', nested && 'pl-7')}>
          <span className="font-mono text-11 text-ink-2">{row.id.slice(0, 8)}</span>
        </td>
        <td className="py-2 pr-3 text-13 text-ink">
          {pageHref ? (
            <Link
              to={pageHref}
              state={pageState}
              onClick={(event) => event.stopPropagation()}
              aria-label={
                unstructuredHref
                  ? 'Open unstructured ingest view'
                  : configBuildHref
                    ? 'Open config build'
                    : 'Open tabular ingest view'
              }
              // Full config name on hover: the fixed-width Type column can
              // only fit the kind, so the sub-line carries the name truncated.
              title={typeCell.title}
              className="flex w-full min-w-0 flex-col items-start text-st-info hover:underline"
            >
              <span className="inline-flex max-w-full items-center gap-1">
                <span className="truncate">{typeCell.label}</span>
                <ArrowUpRight className="size-3 shrink-0" aria-hidden />
              </span>
              {typeCell.subLine ? (
                <span className="block max-w-full truncate text-11 font-normal text-ink-3 no-underline">
                  {typeCell.subLine}
                </span>
              ) : null}
            </Link>
          ) : (
            row.kind
          )}
        </td>
        <td className="min-w-0 py-2 pr-3">
          <span className="block truncate font-mono text-11 text-ink-3">{row.fileName}</span>
        </td>
        <td className="py-2 pr-2 text-11 text-ink-3">{formatRelativeTime(row.submittedAt)}</td>
        <td className="py-2 pr-3">
          <Bar
            value={stateToProgress[row.state] ?? 0}
            status={statusToBar[row.state]}
            className="h-1"
          />
        </td>
        <td className="py-2 pr-3 text-right">
          <span className="inline-flex items-center justify-end gap-2">
            <StatusPill status={tabularPill?.status ?? statusToPill[row.state]}>
              {tabularPill?.label ?? statusToLabel[row.state]}
            </StatusPill>
            {showStop ? (
              <WriteButton
                canManage={canManage}
                type="button"
                variant="ghost"
                size="xs"
                aria-label="Stop ingest"
                title="Stop ingest"
                onClick={(event) => {
                  event.stopPropagation()
                  onCancelClick?.(row)
                }}
                className={cn(
                  'h-auto gap-1 rounded-full border px-2 py-px text-11 font-medium tracking-[0.01em]',
                  'transition-colors duration-200 ease-out motion-reduce:transition-none',
                  'border-st-down/30 bg-st-down-bg text-st-down hover:border-st-down/60 hover:bg-st-down-bg hover:text-st-down',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-st-down/40'
                )}
              >
                Stop
              </WriteButton>
            ) : null}
            {onDeleteClick ? (
              <WriteButton
                canManage={canManage}
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={`Delete ingest job ${row.id}`}
                title="Delete ingest job"
                onClick={(event) => {
                  event.stopPropagation()
                  onDeleteClick(row)
                }}
                className={cn(
                  'text-ink-3 transition-colors duration-200 ease-out motion-reduce:transition-none',
                  'hover:bg-st-down-bg hover:text-st-down',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-st-down/40'
                )}
              >
                <Trash2 className="size-3.5" aria-hidden />
              </WriteButton>
            ) : null}
          </span>
        </td>
      </tr>
      <tr>
        <td colSpan={6} className="p-0">
          <div
            data-open={open}
            className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none data-[open=true]:grid-rows-[1fr]"
          >
            <div className="min-h-0 overflow-hidden">
              {detailMounted ? (
                <div
                  data-open={open}
                  className="border-b border-line bg-bg-sunk/40 px-3 py-2 opacity-0 transition-opacity duration-200 ease-out motion-reduce:transition-none data-[open=true]:opacity-100"
                >
                  <JobDetailPanel kind={row.kind} id={row.id} />
                </div>
              ) : null}
            </div>
          </div>
        </td>
      </tr>
    </>
  )
}

function ExpansionRow({
  run,
  isOpen,
  expandedJob,
  onJobClick,
  onCancelClick,
  onDeleteClick,
  canManage
}: ExpansionRowProps) {
  const [isMounted, setIsMounted] = React.useState(isOpen)
  const nestedJobs = React.useMemo(() => nestPlcZipJobs(run.jobs), [run.jobs])
  const nestedChildCount = nestedJobs.reduce((sum, entry) => sum + entry.children.length, 0)

  React.useEffect(() => {
    if (isOpen) {
      setIsMounted(true)
      return undefined
    }
    const timer = setTimeout(() => setIsMounted(false), EXPAND_MS)
    return () => clearTimeout(timer)
  }, [isOpen])

  return (
    <tr className={isOpen ? 'border-b border-line' : ''}>
      <td colSpan={COLUMN_COUNT} className="p-0">
        <div
          data-open={isOpen}
          className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none data-[open=true]:grid-rows-[1fr]"
        >
          <div className="min-h-0 overflow-hidden">
            <div
              data-open={isOpen}
              className="bg-bg-sunk px-4 py-3 opacity-0 transition-opacity duration-200 ease-out motion-reduce:transition-none data-[open=true]:opacity-100"
            >
              {isMounted ? (
                <div className="flex flex-col gap-2">
                  <div className="text-11 font-medium tracking-[0.05em] text-ink-3 uppercase">
                    {nestedJobs.length} job{nestedJobs.length === 1 ? '' : 's'} in this run
                    {nestedChildCount > 0
                      ? ` · ${nestedChildCount} PLC file${nestedChildCount === 1 ? '' : 's'}`
                      : ''}
                  </div>
                  <div className="overflow-x-auto rounded-md border border-line bg-bg-elev">
                    <table className="w-full min-w-190 table-fixed text-left">
                      <colgroup>
                        <col className="w-22.5" />
                        <col className="w-32.5" />
                        <col />
                        <col className="w-22.5" />
                        <col className="w-25" />
                        <col className="w-40" />
                      </colgroup>
                      <thead className="border-b border-line text-ink-3">
                        <tr>
                          <th className="px-3 py-2 text-10 font-medium tracking-[0.06em] uppercase">
                            Job
                          </th>
                          <th className="py-2 pr-3 text-10 font-medium tracking-[0.06em] uppercase">
                            Type
                          </th>
                          <th className="py-2 pr-3 text-10 font-medium tracking-[0.06em] uppercase">
                            File
                          </th>
                          <th className="py-2 pr-2 text-10 font-medium tracking-[0.06em] uppercase">
                            Started
                          </th>
                          <th className="py-2 pr-3 text-10 font-medium tracking-[0.06em] uppercase">
                            Progress
                          </th>
                          <th className="py-2 pr-3 text-right text-10 font-medium tracking-[0.06em] uppercase">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {nestedJobs.map(({ job: row, children }) => (
                          <React.Fragment key={`${row.kind}-${row.id}`}>
                            <JobTableRow
                              row={row}
                              open={expandedJob?.kind === row.kind && expandedJob.id === row.id}
                              onJobClick={onJobClick}
                              onCancelClick={onCancelClick}
                              onDeleteClick={onDeleteClick}
                              canManage={canManage}
                            />
                            {children.map((child) => (
                              <JobTableRow
                                key={`${child.kind}-${child.id}`}
                                row={child}
                                nested
                                open={
                                  expandedJob?.kind === child.kind && expandedJob.id === child.id
                                }
                                onJobClick={onJobClick}
                                onCancelClick={onCancelClick}
                                onDeleteClick={onDeleteClick}
                                canManage={canManage}
                              />
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {run.message ? (
                    <div
                      className={cn(
                        'rounded-md px-2.5 py-2 text-xs leading-relaxed',
                        run.state === 'error'
                          ? 'bg-st-down-bg text-st-down'
                          : 'bg-st-warn-bg text-st-warn'
                      )}
                    >
                      {run.message}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </td>
    </tr>
  )
}

export function JobsTable({
  scope = 'all',
  activeBranchId,
  siteNames = {},
  siteId,
  siteName
}: JobsTableProps) {
  const dispatch = useAppDispatch()
  const canManage = useGetAuthzMeQuery().data?.permissions.includes('ingest/manage') === true
  const debugMode = useDebugMode()
  const [cancelIngestJob, cancelIngestJobState] = useCancelIngestJobMutation()
  const [renameIngestRun] = useRenameIngestRunMutation()
  const [cancelTarget, setCancelTarget] = React.useState<IngestJobRow | null>(null)
  const lastCancelTargetRef = React.useRef<IngestJobRow | null>(null)
  const [deleteTarget, setDeleteTarget] = React.useState<IngestJobRow | null>(null)
  const lastDeleteTargetRef = React.useRef<IngestJobRow | null>(null)
  const [renameTarget, setRenameTarget] = React.useState<IngestRunRow | null>(null)

  const [page, setPage] = React.useState(0)
  const [expandedRunId, setExpandedRunId] = React.useState<string | null>(null)
  const [expandedJob, setExpandedJob] = React.useState<{ kind: IngestJobKind; id: string } | null>(
    null
  )
  // Intake runs are listed on each intake's ledger. The branch view keeps them
  // so it still shows everything that wrote to the branch.
  const scopeFilter =
    scope === 'branch'
      ? activeBranchId
        ? { associatedBranchId: activeBranchId }
        : {}
      : { excludeSourceKind: 'intake' as const }
  const jobsQ = useGetIngestJobsPageQuery(
    {
      kind: 'all',
      page,
      pageSize: PAGE_SIZE,
      ...scopeFilter
    },
    {
      skip: scope === 'branch' && !activeBranchId,
      refetchOnMountOrArgChange: true
    }
  )

  const seenIdsRef = React.useRef<Set<string> | null>(null)

  React.useEffect(() => {
    setPage(0)
    setExpandedRunId(null)
    setExpandedJob(null)
    seenIdsRef.current = null
  }, [activeBranchId, scope, siteId])

  const hadActive = React.useRef(false)

  React.useEffect(() => {
    const jobs: JobListItem[] = jobsQ.data?.jobs ?? []
    // Work-orders never write the graph; every other active ingest family
    // (including unstructured agent runs) does, so completion must invalidate.
    const graphAffectingHasActive = jobs.some(
      (j) => j.kind !== 'work_orders' && isActiveStatus(j.state)
    )
    if (hadActive.current && !graphAffectingHasActive && jobs.length > 0) {
      invalidateGraphData(dispatch)
    }
    hadActive.current = graphAffectingHasActive
  }, [jobsQ.data, dispatch])

  const merged = React.useMemo(
    () => mergeIngestJobItems(jobsQ.data?.jobs ?? []),
    [jobsQ.data?.jobs]
  )
  const runs = React.useMemo(() => {
    const all = groupIngestRuns(merged)
    if (!siteId?.trim()) return all
    return all.filter((run) =>
      run.jobs.some((job) => jobBelongsToSiteFilter(job.siteId, siteId, siteName))
    )
  }, [merged, siteId, siteName])

  // `/ingest?job=<id>` (from a notification) opens that job once it loads.
  const [searchParams] = useSearchParams()
  const focusJobId = searchParams.get('job')
  const focusedRef = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (!focusJobId || focusedRef.current === focusJobId) return
    const hit = findRunForJob(runs, focusJobId)
    if (!hit) return
    focusedRef.current = focusJobId
    setExpandedRunId(hit.runId)
    setExpandedJob({ kind: hit.job.kind, id: hit.job.id })
  }, [focusJobId, runs])

  const errors: string[] = []
  if (jobsQ.error) errors.push('Failed to load ingest jobs.')

  const isLoading = jobsQ.isLoading
  const hasNext = jobsQ.data?.hasNext ?? false
  const start = page * PAGE_SIZE + 1
  const end = page * PAGE_SIZE + runs.length

  // Auto-expand newly-arrived runs (shared groupId or singleton job id).
  React.useEffect(() => {
    const currentIds = new Set(runs.map((r) => r.id))
    if (seenIdsRef.current === null) {
      seenIdsRef.current = currentIds
      return
    }
    for (const run of runs) {
      if (!seenIdsRef.current.has(run.id)) {
        setExpandedRunId(run.id)
        break
      }
    }
    seenIdsRef.current = currentIds
  }, [runs])

  const siteLabel = React.useCallback(
    (siteId: string) => {
      if (!siteId || siteId === '—' || siteId === '-') return '—'
      return siteOptionLabel(siteNames[siteId] ?? siteId, siteId)
    },
    [siteNames]
  )

  const handleRunClick = (run: IngestRunRow) => {
    setExpandedJob(null)
    setExpandedRunId((cur) => (cur === run.id ? null : run.id))
  }

  const handleJobClick = (row: IngestJobRow) => {
    setExpandedJob((cur) =>
      cur?.kind === row.kind && cur.id === row.id ? null : { kind: row.kind, id: row.id }
    )
  }

  const handleRenameRun = async (runId: string, name: string) => {
    await renameIngestRun({ runId, name }).unwrap()
  }

  const handleCancelConfirm = async (scope: 'job' | 'group' = 'job') => {
    if (!cancelTarget) return
    const { id } = cancelTarget
    // Abort the local XHR first (snappy local feedback) — no-op if no
    // upload is in flight. The server endpoint then converges the
    // row to terminal `cancelled` regardless of whether the XHR was
    // mid-flight or already landed.
    abortIngestUpload(id)
    try {
      await cancelIngestJob(scope === 'group' ? { jobId: id, scope } : id).unwrap()
    } catch {
      // The polling refetch will surface the authoritative server
      // state; nothing actionable to show inline. Errors are rare
      // (404 / 409 / network blip).
    }
    setCancelTarget(null)
  }
  const dialogTarget = cancelTarget ?? lastCancelTargetRef.current
  const dialogTargetIsGroupedPlc = dialogTarget?.kind === 'PLC' && Boolean(dialogTarget.groupId)
  if (deleteTarget) {
    lastDeleteTargetRef.current = deleteTarget
  }
  const deleteDialogTarget = deleteTarget ?? lastDeleteTargetRef.current
  const emptyTitle = siteId?.trim()
    ? 'No ingest runs for this site'
    : scope === 'branch'
      ? 'No ingest runs on this branch'
      : 'No ingest runs yet'
  const emptyHint = siteId?.trim()
    ? 'Try All sites, another site, or All branches if the run landed elsewhere.'
    : scope === 'branch'
      ? 'Switch to All branches to see ingest history across branches.'
      : 'Start a new ingest to add or modify a site.'

  const goToPreviousPage = () => {
    setExpandedRunId(null)
    setExpandedJob(null)
    seenIdsRef.current = null
    setPage((current) => Math.max(0, current - 1))
  }

  const goToNextPage = () => {
    if (!hasNext) return
    setExpandedRunId(null)
    setExpandedJob(null)
    seenIdsRef.current = null
    setPage((current) => current + 1)
  }

  const runActionIcon = (action: IngestRunRow['action']) => {
    if (action === 'Add site') return <Plus className="size-3.5 shrink-0" aria-hidden />
    if (action === 'Modify site') return <PencilLine className="size-3.5 shrink-0" aria-hidden />
    return null
  }

  return (
    <Card className="gap-0 overflow-hidden py-0">
      {errors.length > 0 ? (
        <div className="flex flex-col gap-2 px-4 pt-4">
          {errors.map((message, i) => (
            <div
              key={`err-${i}`}
              role="alert"
              className="rounded-md bg-st-down-bg px-3 py-2 text-sm text-st-down"
            >
              {message}
            </div>
          ))}
        </div>
      ) : null}

      <div className="overflow-x-auto">
        {/* table-fixed pins column widths to the <th> sizes below. Without
            it, the browser would re-measure widths based on content in any
            row — including the expansion <td colSpan={6}> — and child job
            panels would shift the entire table's column layout when expanded. */}
        <table className="w-full table-fixed text-left text-sm">
          <thead className="border-b border-line text-ink-3">
            <tr>
              <th className="w-22.5 px-4 py-2 text-xs font-medium uppercase tracking-wider">Run</th>
              <th className="w-32.5 py-2 pr-3 text-xs font-medium uppercase tracking-wider">
                Action
              </th>
              <th className="w-35 py-2 pr-3 text-xs font-medium uppercase tracking-wider">Site</th>
              <th className="w-22.5 py-2 pr-1 text-xs font-medium uppercase tracking-wider">
                Started
              </th>
              <th className="w-25 py-2 pr-2 text-xs font-medium uppercase tracking-wider">
                Progress
              </th>
              <th className="w-35 py-2 pr-4 pl-2 text-right text-xs font-medium uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => {
              const isOpen = expandedRunId === run.id
              const barStatus = statusToBar[run.state]
              return (
                <React.Fragment key={run.id}>
                  <ContextMenu>
                    <ContextMenuTrigger asChild>
                      <tr
                        onClick={() => handleRunClick(run)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            handleRunClick(run)
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-expanded={isOpen}
                        data-expanded={isOpen}
                        className={cn(
                          'cursor-pointer border-b border-line transition-colors hover:bg-bg-sunk focus-visible:bg-bg-sunk focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30',
                          isOpen && 'bg-bg-sunk'
                        )}
                      >
                        <td className="px-4 py-2">
                          {run.runName ? (
                            <span className="block min-w-0">
                              <span className="block truncate text-sm font-medium text-ink">
                                {run.runName}
                              </span>
                              <span className="block font-mono text-10 text-ink-3">
                                {run.displayId}
                              </span>
                            </span>
                          ) : (
                            <span className="font-mono text-xs text-ink-2">{run.displayId}</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-sm text-ink">
                          <span className="inline-flex items-center gap-1.5">
                            {runActionIcon(run.action)}
                            {run.action}
                          </span>
                        </td>
                        <td
                          className="overflow-hidden py-2 pr-3 text-xs text-ink-2"
                          title={run.siteId !== '—' ? run.siteId : undefined}
                        >
                          <div className="truncate">{siteLabel(run.siteId)}</div>
                        </td>
                        <td className="py-2 pr-1 text-xs text-ink-3">
                          {formatRelativeTime(run.submittedAt)}
                        </td>
                        <td className="py-2 pr-2">
                          <Bar
                            value={run.progress}
                            status={barStatus}
                            ariaLabel={`${runStatusLabel(run)} progress`}
                          />
                        </td>
                        <td className="py-2 pr-4 pl-2 text-right">
                          <StatusPill status={runStatusPill(run)}>{runStatusLabel(run)}</StatusPill>
                        </td>
                      </tr>
                    </ContextMenuTrigger>
                    <ContextMenuContent>
                      <ContextMenuItem
                        onSelect={(event) => {
                          event.stopPropagation()
                          setRenameTarget(run)
                        }}
                      >
                        <PencilLine />
                        {run.runName ? 'Rename run' : 'Name run'}
                      </ContextMenuItem>
                    </ContextMenuContent>
                  </ContextMenu>
                  <ExpansionRow
                    run={run}
                    isOpen={isOpen}
                    expandedJob={expandedJob}
                    onJobClick={handleJobClick}
                    onCancelClick={setCancelTarget}
                    onDeleteClick={debugMode ? setDeleteTarget : undefined}
                    canManage={canManage}
                  />
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {!isLoading && runs.length === 0 ? (
        <Empty title={emptyTitle} hint={emptyHint} />
      ) : (
        <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-2 text-xs text-ink-3">
          <span>
            {runs.length > 0
              ? `Showing runs ${start}-${end}, sorted by newest first.`
              : 'No runs on this page.'}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              disabled={page === 0 || jobsQ.isFetching}
              onClick={goToPreviousPage}
            >
              Previous
            </Button>
            <span className="min-w-16 text-center">Page {page + 1}</span>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              disabled={!hasNext || jobsQ.isFetching}
              onClick={goToNextPage}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      <AlertDialog
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {dialogTargetIsGroupedPlc ? 'Stop PLC ingest jobs?' : 'Stop this ingest?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {dialogTarget
                ? dialogTargetIsGroupedPlc
                  ? `${dialogTarget.fileName} was started from a multi-file PLC upload. Stop only this job or every active PLC job from that upload. Partial graph data is not rolled back; re-upload to recover.`
                  : `${dialogTarget.fileName} — any work in progress will be discarded. Partial graph data is not rolled back; re-upload to recover.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-line bg-bg-body text-ink-2 hover:bg-bg-sunk hover:text-ink dark:border-ink-3 dark:bg-bg-sunk dark:text-ink-2 dark:hover:bg-bg-body dark:hover:text-ink"
              disabled={cancelIngestJobState.isLoading}
            >
              Cancel
            </AlertDialogCancel>
            {dialogTargetIsGroupedPlc ? (
              <AlertDialogAction
                variant="outline"
                className="border-line bg-bg-body text-ink hover:bg-bg-sunk hover:text-ink dark:border-ink-3 dark:bg-bg-sunk dark:text-ink dark:hover:bg-bg-body dark:hover:text-ink"
                disabled={cancelIngestJobState.isLoading}
                onClick={(event) => {
                  event.preventDefault()
                  void handleCancelConfirm('job')
                }}
              >
                Stop this job
              </AlertDialogAction>
            ) : null}
            <AlertDialogAction
              variant="outline"
              className="border-st-down bg-st-down text-white shadow-sm hover:bg-st-down/90 hover:text-white dark:border-st-down dark:bg-st-down dark:text-white dark:hover:bg-st-down/90 dark:hover:text-white"
              disabled={cancelIngestJobState.isLoading}
              onClick={(event) => {
                event.preventDefault()
                void handleCancelConfirm(dialogTargetIsGroupedPlc ? 'group' : 'job')
              }}
            >
              {dialogTargetIsGroupedPlc ? 'Stop all jobs' : 'Stop ingest'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {deleteDialogTarget ? (
        <IngestJobDeleteDialog
          jobId={deleteDialogTarget.id}
          fileName={deleteDialogTarget.fileName}
          jobState={deleteDialogTarget.state}
          open={deleteTarget !== null}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null)
          }}
          onDeleted={() =>
            setExpandedJob((current) => (current?.id === deleteDialogTarget.id ? null : current))
          }
        />
      ) : null}
      <RenameIngestRunDialog
        run={renameTarget}
        onClose={() => setRenameTarget(null)}
        onSubmit={handleRenameRun}
      />
    </Card>
  )
}
